<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else class="container">
      <span class="form-title">
        <span>Create</span>
        <span>event</span>
      </span>
      <form v-scroll-to-error @keydown.enter.prevent="">
        <div class="steps">
          <span :class="activeStep === 1 ? 'curr-step' : 'prev-step'" @click="activeStep = 1">
            {{ '1. Event Info' }}
          </span>
          <hr :class="activeStep > 1 ? 'hr-active' : 'hr'">
          <span
            :class="['step', activeStep === 2 && 'curr-step', activeStep === 3 && 'prev-step']"
            @click=";(activeStep = 2), moduleCheck()"
          >
            {{
              isRegularOrganization() || isSportOrganization() ? '2. Modules' : '2. Dates & Tickets'
            }}
          </span>
        </div>
        <div v-if="activeStep === 1">
          <event-info-form
            v-if="isSportOrganization() || isRegularOrganization()"
            :vs="$v.form.generalInfo"
            :initial-values="form.generalInfo"
            :categories="allEventCategories"
            @next="activeStep = 2"
            @values="form.generalInfo = $event"
          />
          <theater-event-info-form
            v-else
            :vs="$v.form.generalInfo"
            :initial-values="form.generalInfo"
            :organization-slug="organizationSlug"
            :categories="allEventCategories"
            @next="activeStep = 2"
            @values="form.generalInfo = $event"
          />
        </div>
        <div v-if="activeStep === 2">
          <event-extra-modules
            v-if="isRegularOrganization() || isSportOrganization()"
            :initial-values="form"
            :vs="$v.form"
            :submitting-form="submittingForm"
            @back="activeStep = 1"
            @submit="submitForm()"
          />
          <performance-container
            v-else
            :initial-values="form.performance"
            :organization-slug="organizationSlug"
            :vs="$v.form.performance"
            :submitting-form="submittingForm"
            @removePerformance="form.performance = $event"
            @back="activeStep = 1"
            @createPerformance="handleCreatePerformance"
            @venueChange="venues = $event"
          />
        </div>
      </form>
    </div>
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import {
  between,
  maxLength,
  minLength,
  minValue,
  required,
  requiredIf,
} from 'vuelidate/lib/validators'

import { GET_ALL_EVENT_CATEGORIES } from '@/graphql/eventCategory.graphql'
import { CREATE_ANNUAL_TICKETS_FOR_EVENT, CREATE_EVENT } from '@/graphql/event.graphql'
import { date, httpUrl, maxDate, minDate } from '@/validators'
import { submitGuard, tvornicaKultureCustomOrganizerMsg } from '@/utils'
import { CategoryType, EventCategoryId, OrganizationSlug } from '@/types'

import TheaterEventInfoForm from '@/components/events/theaterEventInfoForm.vue'
import eventInfoForm from './event-info-form.vue'
import eventExtraModules from './event-extra-modules.vue'
import performanceContainer from './performance-container.vue'
import simpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { CREATE_EVENT_TEMPLATE_AND_PERFORMANCE } from '@/graphql/eventTemplate.graphql'
import moment from 'moment'

export default {
  name: 'event-create-container',
  components: {
    eventInfoForm,
    eventExtraModules,
    TheaterEventInfoForm,
    simpleSnackbar,
    performanceContainer,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  validations() {
    return {
      form: {
        generalInfo: {
          eventCategoryId: { required },
          interests: {
            required: requiredIf(() => this.isSubcategoryRequiredField),
          },
          title: { required, maxLength: maxLength(120) },
          instagramLink: {
            httpUrl,
          },
          webLink: {
            httpUrl,
          },
          facebookLink: {
            httpUrl,
          },
          genreId: {
            required: requiredIf(
              () => !this.isSportOrganization() && !this.isRegularOrganization(),
            ),
          },
          durationInMins: {
            required: requiredIf(
              () => !this.isSportOrganization() && !this.isRegularOrganization(),
            ),
          },
          from: {
            required: requiredIf(() => this.isSportOrganization() || this.isRegularOrganization()),
            date,
            minDate: minDate(() => new Date()),
            maxDate: maxDate(() => this.form.generalInfo.to),
          },
          to: {
            required: requiredIf(() => this.isSportOrganization() || this.isRegularOrganization()),
            date,
            minDate: minDate(() => this.form.generalInfo.from),
          },
          location: {
            venue: {
              required: requiredIf(
                () => this.isSportOrganization() || this.isRegularOrganization(),
              ),
            },
            address1: {
              required: requiredIf(
                () => this.isSportOrganization() || this.isRegularOrganization(),
              ),
            },
          },
          highlightedData: {
            ageRestriction: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.ageRestriction.enabled,
                ),
                minValue: minValue(0),
              },
            },
            freeEntranceWithAgeLimit: {
              value: {
                required: requiredIf(
                  () => this.form.generalInfo.highlightedData.freeEntranceWithAgeLimit.enabled,
                ),
                minValue: minValue(0),
              },
            },
          },
          faqs: {
            $each: {
              answer: { required },
              question: { required },
            },
          },
          metadata: {
            webLink: {
              httpUrl,
            },
            facebookLink: {
              httpUrl,
            },
          },
        },
        numberOfZones: {
          required: requiredIf(({ modules }) => modules && modules.categoriesAndZones),
          between: between(1, 10),
        },
        performance: {
          required: requiredIf(() => !this.isSportOrganization() && !this.isRegularOrganization()),
          $each: {
            date: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            time: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            performanceType: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            orderItemTemplateIds: {
              required: requiredIf(
                () => !this.isSportOrganization() && !this.isRegularOrganization(),
              ),
            },
            //TODO find a way to handle this
            audienceCapacity: {
              required(v, obj) {
                let hasNoNumeration = this.venues.length
                  ? this.venues
                    .filter(v => !v.seats || !v.predefinedSections)
                    .map(v => v.id)
                    .includes(obj.venueId)
                  : false

                return hasNoNumeration ? required(v) : required(!v)
              },
            },
            // orderItemTemplateIds: {
            //   minLength: minLength(1),
            // },
          },
        },
        categories: {
          $each: {
            form: {
              name: { required },
              color: { required },
              zones: {
                required,
                minLength: minLength(1),
                maxLength: maxLength(10),
              },
            },
          },
        },
      },
    }
  },
  data: () => ({
    snackbar: null,
    activeStep: 1,
    CategoryType,
    EventCategoryId,
    isSubcategoryRequiredField: false,
    form: {
      generalInfo: {
        title: null,
        from: null,
        to: null,
        metadata: {
          webLink: '',
          facebookLink: '',
        },
        eventCategoryId: null,
        interests: null,
        location: {
          latitude: 45.8087407,
          longitude: 15.9740865,
        },
        cover: null,
        description: null,
        organizerMessage: null,
        highlightedData: {
          ageRestriction: {
            show: false,
            enabled: false,
            value: 0,
          },
          freeEntranceWithAgeLimit: {
            show: false,
            enabled: false,
            value: 0,
          },
          tableReservation: {
            show: false,
            enabled: false,
          },
        },
        faqs: [],
      },
      performance: [],
      categories: [],
      modules: {},
      numberOfZones: 1,
    },
    allEventCategories: [],
    venues: [],
    submittingForm: false,
  }),
  apollo: {
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          if (this.isRegularOrganization() || this.isSportOrganization()) {
            this.allEventCategories = data.getAllEventCategories.filter(
              category => category.name !== this.CategoryType.THEATER,
            )
          } else {
            this.allEventCategories = data.getAllEventCategories.sort((a, b) => a.id - b.id)
          }
        }
      },
    },
  },
  watch: {
    'form.generalInfo.eventCategoryId': function () {
      const category = this.allEventCategories.find(
        c => c.id === this.form.generalInfo.eventCategoryId,
      )
      if (!category) {
        return (this.isSubcategoryRequiredField = false)
      }
      const subCategories = [...category.subCategories].filter(sc => !sc.organizationId)

      return (this.isSubcategoryRequiredField = subCategories.length)
    },
  },
  mounted() {
    if (this.isSportOrganization()) {
      this.form.modules = { ticketing: true }
    }
    if (this.organizationSlug === OrganizationSlug.TVORNICA_KULTURE) {
      this.form.generalInfo.organizerMessage = tvornicaKultureCustomOrganizerMsg
    }
  },
  methods: {
    async handleCreatePerformance(performanceInfo) {
      //todo fix validation , add input error colors within performances
      //todo fix bug on category change - second step doesn't work properly

      this.$v.form.$touch()

      if (!this.$v.form.performance.$model.length) {
        return (this.snackbar = {
          open: true,
          messageProp: 'At least one performance is required.',
          color: 'error',
        })
      }

      if (this.$v.form.$invalid === true) {
        return (this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information',
          color: 'error',
        })
      }
      await submitGuard(this, async () => {
        this.submittingForm = true
        const performanceInput = this.performanceDataEdit(performanceInfo)

        // delete this.form.generalInfo.metadata
        this.form.generalInfo.eventCategoryId = Number(this.form.generalInfo.eventCategoryId)
        const { to, from, location, organizerMessage, metadata, ...templateInfo } =
          this.form.generalInfo

        const eventTemplateInput = { ...templateInfo }

        // Metadata object should not be in the request in case of theater organization
        if (this.isSportOrganization() || this.isRegularOrganization()) {
          Object.assign(eventTemplateInput, metadata)
        }

        const { errors } = await this.$apollo.mutate({
          mutation: CREATE_EVENT_TEMPLATE_AND_PERFORMANCE,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateInput,
            performanceInput,
          },
        })
        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: 'Oops, Something went wrong!',
            color: 'error',
          }
          this.submittingForm = false
        } else {
          this.goTo(this.routes.theaterOrganization.theaterLiveEvents, {
            organizationSlug: this.organizationSlug,
            eventCreated: true,
          })
        }
      })
    },
    performanceDataEdit(data) {
      return data.map(data => {
        const { time, date, ...info } = data
        const formatedDate = moment(date).format('YYYY-MM-DD')

        return {
          ...info,
          from: moment(`${formatedDate} ${time}`),
        }
      })
    },
    moduleCheck() {
      if (!this.form.generalInfo.eventCategoryId) return

      if (
        this.allEventCategories.find(c => parseInt(c.id) === this.form.generalInfo.eventCategoryId)
          .name === this.CategoryType.MUSIC
      ) {
        this.form.modules = {
          ...this.form.modules,
          ticketing: true,
          categoriesAndZones: this.ticketing ? false : this.categoriesAndZones,
        }
      }
    },
    async submitForm() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        return (this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information',
          color: 'error',
        })
      }
      await submitGuard(this, async () => {
        this.submittingForm = true
        const values = this.form

        const { categories: categoriesForm, generalInfo, modules, numberOfZones } = values

        const {
          to,
          from,
          location,
          title,
          description,
          cover,
          interests,
          metadata,
          highlightedData,
          faqs,
          eventCategoryId,
        } = generalInfo

        const baseEventData = {
          title,
          to,
          from,
          numberOfZones,
          description,
          cover,
          metadata,
          highlightedData,
          faqs,
        }

        const extraModules = {
          categoriesAndZones: modules && modules.categoriesAndZones,
          mobileApp: modules && modules.mobileApp,
          conferenceMobileApp: modules && modules.conferenceMobileApp,
          expoMobileApp: modules && modules.expoMobileApp,
          workshops: modules && modules.workshops,
          ticketing: modules && !modules.categoriesAndZones && modules.ticketing,
          floorPlan: modules && modules.floorPlan,
          ticketPersonalization: modules && modules.ticketPersonalization,
        }
        const categories = (categoriesForm || []).map(({ form }) => form)
        const {
          errors,
          data: {
            createEvent: { id },
          },
        } = await this.$apollo.mutate({
          mutation: CREATE_EVENT,
          variables: {
            event: {
              baseEventData,
              interests,
              eventCategoryId,
              location,
              extraModules,
              categories,
            },
            organizationSlug: this.organizationSlug,
          },
          refetchQueries: ['getEventsAndQuota', 'getOrganization', 'getOrganizations'],
        })

        if (errors && errors[0]) {
          this.snackbar = {
            open: true,
            messageProp: errors[0].message,
            color: 'error',
          }
          this.submittingForm = false
        } else {
          if (this.isSportOrganization()) {
            await this.$apollo.mutate({
              mutation: CREATE_ANNUAL_TICKETS_FOR_EVENT,
              variables: { eventId: id },
            })
            this.goTo(this.routes.sportOrganization.sportLiveEvents, {
              organizationSlug: this.organizationSlug,
              eventCreated: true,
            })
          } else {
            this.goTo(this.routes.organization.liveEvents, {
              organizationSlug: this.organizationSlug,
              eventCreated: true,
            })
          }
        }
      })
    },
    isRegularOrganization() {
      return this.$route.path.includes('/organizations/')
    },
    isSportOrganization() {
      return this.$route.path.includes('/sport-organizations/')
    },
  },
}
</script>

<style scoped>
.form-title {
  height: fit-content;
  color: #ff4c00;
  display: flex;
  flex-direction: column;
  font-family: 'Raleway', sans-serif;
  font-size: 2.25rem;
  font-weight: bold;
  margin-right: 3rem;
}

.container {
  padding-top: 0.75rem !important;
  display: flex;
}

.steps {
  font-family: 'Raleway';
  margin-bottom: 2rem;
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #d9d9d9;
  cursor: pointer;
}

.curr-step {
  font-family: 'Raleway';
  cursor: pointer;
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #0277bd !important;
  margin-bottom: 0.75rem;
}

.prev-step {
  cursor: pointer;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #979797 !important;
}

.hr {
  width: 2.25rem;
  height: 0.25rem;
  display: inline-block;
  background: #d9d9d9;
  border: none;
  border-radius: 0.5rem;
  vertical-align: middle;
  margin: 0 0.75rem;
  cursor: default;
}

.hr-active {
  width: 2.25rem;
  height: 0.25rem;
  border: none;
  border-radius: 0.5rem;
  display: inline-block;
  background: #979797;
  vertical-align: middle;
  cursor: default;
  margin: 0 0.75rem;
}

@media screen and (max-width: 960px) {
  .container {
    display: flex;
    padding: 2rem;
    flex-direction: column;
  }

  .form-title {
    margin-bottom: 1.5rem;
    column-gap: 0.5rem;
    flex-direction: row;
  }
}

@media screen and (min-width: 1904px) {
  .container {
    max-width: 74.063rem !important;
  }
}

form {
  width: min(43.52rem, 100%);
}
</style>
